import type { icons } from "lucide-react";
import { useCallback } from "react";

import { Icon } from "~/components/icon";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { cn } from "~/utils";

export function CopyButton({
  value,
  label,
  icon = "Copy",
  size = 14,
  className,
}: {
  value: string;
  label: string;
  /** Idle icon; swaps to a check while copied. */
  icon?: keyof typeof icons;
  size?: number;
  className?: string;
}) {
  const [copiedText, copy, resetCopiedText] = useCopyToClipboard();
  const copied = copiedText === value;

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      // These buttons often sit inside clickable rows or forms.
      e.preventDefault();
      e.stopPropagation();
      const success = await copy(value);
      if (success) setTimeout(resetCopiedText, 2000);
    },
    [value, copy, resetCopiedText],
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn("shrink-0 text-gray-400 hover:text-gray-600", className)}
      title={copied ? "Copied!" : label}
    >
      <Icon
        name={copied ? "Check" : icon}
        size={size}
        className={copied ? "text-green-500" : ""}
      />
    </button>
  );
}
